import { z } from 'zod';

export {
  createWordChantStyleForPreset,
  DEFAULT_WORD_CHANT_STYLE,
  WORD_CHANT_FONT_IDS,
  WORD_CHANT_PALETTE_IDS,
  WORD_CHANT_TEXT_SCALES,
  WORD_CHANT_VISUAL_PRESET_IDS,
  WORD_CHANT_VISUAL_PRESETS,
  wordChantStyleSchema,
} from './word-chant-style-catalog';
export type {
  WordChantFontId,
  WordChantPaletteId,
  WordChantStyle,
  WordChantTextScale,
  WordChantVisualPresetId,
} from './word-chant-style-catalog';

export const WORD_CHANT_FPS = 24;
export const WORD_CHANT_MIN_WORD_COUNT = 3;
export const WORD_CHANT_MAX_WORD_COUNT = 8;

export const WORD_CHANT_INTRO_FRAMES = 180;
export const WORD_CHANT_NORMAL_WORD_FRAMES = 96;
export const WORD_CHANT_INTERLUDE_FRAMES = 135;
export const WORD_CHANT_FAST_WORD_FRAMES = 84;
export const WORD_CHANT_OUTRO_FRAMES = 117;

export const WORD_CHANT_NORMAL_BEAT_FRAMES = 24;
export const WORD_CHANT_FAST_BEAT_FRAMES = 21;

export function resolveWordChantDefaultCueDisplayText(
  text: string,
): string | null {
  const normalized = text
    .trim()
    .toLowerCase()
    .replace(/[^a-z\s]/g, '')
    .replace(/\s+/g, ' ');
  if (normalized === 'word chant') return null;
  if (normalized === 'ready') return 'Ready';
  if (normalized === 'set') return 'Set';
  if (normalized === 'go') return 'Go!';
  if (normalized === 'great') return 'Great!';
  if (normalized.includes('faster')) return 'Faster!';
  if (normalized === 'good job') return 'Good job!';
  return text.trim();
}

const wordChantVisualSourceSchema = z
  .object({
    mode: z.enum(['generate', 'upload']),
    contentRef: z.string().trim().min(1).nullable().optional().default(null),
    fileName: z.string().trim().min(1).max(255).nullable().optional(),
    previewUrl: z
      .string()
      .trim()
      .url()
      .startsWith('https://')
      .max(2048)
      .nullable()
      .optional(),
  })
  .strict()
  .superRefine((source, context) => {
    if (source.mode === 'upload' && source.contentRef === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contentRef'],
        message: 'contentRef is required when mode is upload',
      });
    }
    if (source.mode === 'generate' && source.contentRef !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contentRef'],
        message: 'contentRef must be null when mode is generate',
      });
    }
    if (
      source.mode === 'generate' &&
      (source.fileName != null || source.previewUrl != null)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fileName'],
        message: 'upload display metadata must be empty when mode is generate',
      });
    }
  });

const wordChantWordSchema = z
  .object({
    wordId: z.string().trim().min(1).max(100),
    text: z
      .string()
      .trim()
      .min(1)
      .max(80)
      .regex(/^[A-Za-z]+(?:[-'][A-Za-z]+)*$/, {
        message: 'text must be one English word',
      }),
    order: z.number().int().min(1).max(WORD_CHANT_MAX_WORD_COUNT),
    visual: z
      .object({
        illustration: wordChantVisualSourceSchema,
        photo: wordChantVisualSourceSchema,
      })
      .strict(),
  })
  .strict();

export const wordChantInputSchema = z
  .object({
    version: z.literal('word_chant.input.v1'),
    title: z.string().trim().min(1).max(100),
    languageCode: z.literal('en-US'),
    templateId: z.literal('word-chant-v1'),
    words: z
      .array(wordChantWordSchema)
      .min(WORD_CHANT_MIN_WORD_COUNT)
      .max(WORD_CHANT_MAX_WORD_COUNT),
    chant: z
      .object({
        rounds: z.tuple([z.literal('normal'), z.literal('fast')]),
        repeatCount: z.literal(4),
        teacherRepeatCount: z.literal(2),
        childRepeatCount: z.literal(2),
        promptProfileVersion: z.literal('word-chant-en-v2'),
      })
      .strict(),
    voice: z
      .object({
        audioModel: z.literal('gemini-3.1-flash-tts-preview'),
        teacherVoiceId: z.string().trim().min(1),
        childVoiceId: z.string().trim().min(1),
        teacherNativeVoiceId: z.string().trim().min(1).nullable().optional(),
        childNativeVoiceId: z.string().trim().min(1).nullable().optional(),
        teacherProviderVoiceId: z.string().trim().min(1).nullable().optional(),
        childProviderVoiceId: z.string().trim().min(1).nullable().optional(),
        teacherTtsFallbackCandidates: z
          .array(z.record(z.string(), z.unknown()))
          .default([]),
        childTtsFallbackCandidates: z
          .array(z.record(z.string(), z.unknown()))
          .default([]),
      })
      .strict(),
    output: z
      .object({
        aspectRatio: z.literal('16:9'),
        width: z.literal(1920),
        height: z.literal(1080),
        fps: z.literal(24),
      })
      .strict(),
  })
  .strict()
  .superRefine((input, context) => {
    const wordIds = input.words.map((word) => word.wordId);
    if (new Set(wordIds).size !== wordIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['words'],
        message: 'wordId must be unique',
      });
    }

    const orders = input.words.map((word) => word.order).sort((a, b) => a - b);
    const expectedOrders = input.words.map((_, index) => index + 1);
    if (orders.some((order, index) => order !== expectedOrders[index])) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['words'],
        message: 'order must be unique and contiguous from 1',
      });
    }

    if (input.voice.teacherVoiceId === input.voice.childVoiceId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['voice', 'childVoiceId'],
        message: 'teacherVoiceId and childVoiceId must be different',
      });
    }
  });

export const wordChantAnalysisCueSegmentSchema = z
  .object({
    cueId: z.string().trim().min(1).max(100),
    text: z.string().trim().min(1).max(200),
    startMs: z.number().int().min(0),
    endMs: z.number().int().positive(),
    startFrame: z.number().int().min(0),
    endFrame: z.number().int().positive(),
  })
  .strict()
  .superRefine((cue, context) => {
    if (cue.endMs <= cue.startMs) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endMs'],
        message: 'endMs must be greater than startMs',
      });
    }
    if (cue.endFrame <= cue.startFrame) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endFrame'],
        message: 'endFrame must be greater than startFrame',
      });
    }
  });

export const wordChantEditableCueSegmentSchema = z
  .object({
    cueId: z.string().trim().min(1).max(100),
    detectedText: z.string().trim().min(1).max(200),
    text: z.string().trim().min(1).max(200),
    isEdited: z.boolean().default(false),
    startMs: z.number().int().min(0),
    endMs: z.number().int().positive(),
  })
  .strict()
  .superRefine((cue, context) => {
    if (cue.endMs <= cue.startMs) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endMs'],
        message: 'endMs must be greater than startMs',
      });
    }
  });

export const wordChantAnalysisSchema = z
  .object({
    version: z.literal('word_chant.analysis.v1'),
    sourceAudio: z.record(z.string(), z.unknown()),
    words: z
      .array(
        z
          .object({
            wordId: z.string().trim().min(1),
            detectedText: z.string().trim().min(1),
            normalizedText: z.string().trim().min(1),
            order: z.number().int().positive(),
            occurrences: z.record(z.string(), z.unknown()),
          })
          .strict(),
      )
      .min(WORD_CHANT_MIN_WORD_COUNT)
      .max(WORD_CHANT_MAX_WORD_COUNT),
    cueSegments: z.array(wordChantAnalysisCueSegmentSchema),
  })
  .strict();

export const wordChantRecordedInputSchema = z
  .object({
    version: z.literal('word_chant.input.v2'),
    title: z.string().trim().min(1).max(100),
    languageCode: z.literal('en-US'),
    templateId: z.literal('word-chant-v1'),
    words: z
      .array(
        wordChantWordSchema.extend({
          detectedText: z.string().trim().min(1).max(80),
        }),
      )
      .min(WORD_CHANT_MIN_WORD_COUNT)
      .max(WORD_CHANT_MAX_WORD_COUNT),
    cueSegments: z.array(wordChantEditableCueSegmentSchema).default([]),
    output: z
      .object({
        aspectRatio: z.literal('16:9'),
        width: z.literal(1920),
        height: z.literal(1080),
        fps: z.literal(24),
      })
      .strict(),
  })
  .strict()
  .superRefine((input, context) => {
    const wordIds = input.words.map((word) => word.wordId);
    if (new Set(wordIds).size !== wordIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['words'],
        message: 'wordId must be unique',
      });
    }
    if (input.words.some((word, index) => word.order !== index + 1)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['words'],
        message: 'order must be unique and contiguous from 1',
      });
    }
    const cueIds = input.cueSegments.map((cue) => cue.cueId);
    if (new Set(cueIds).size !== cueIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cueSegments'],
        message: 'cueId must be unique',
      });
    }
    if (
      input.cueSegments.some(
        (cue, index, cues) =>
          index > 0 && cue.startMs < (cues[index - 1]?.startMs ?? 0),
      )
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cueSegments'],
        message: 'cueSegments must be ordered by startMs',
      });
    }
  });

const wordChantStaticTimelineSceneSchema = z
  .object({
    sceneId: z.string().trim().min(1),
    kind: z.enum(['intro', 'interlude', 'outro']),
    startFrame: z.number().int().min(0),
    endFrame: z.number().int().positive(),
  })
  .strict()
  .refine((scene) => scene.endFrame > scene.startFrame, {
    path: ['endFrame'],
    message: 'endFrame must be greater than startFrame',
  });

const wordChantWordTimelineSceneSchema = z
  .object({
    sceneId: z.string().trim().min(1),
    kind: z.literal('word'),
    round: z.enum(['normal', 'fast']),
    wordId: z.string().trim().min(1),
    startFrame: z.number().int().min(0),
    endFrame: z.number().int().positive(),
    cueFrames: z.tuple([
      z.number().int().min(0),
      z.number().int().min(0),
      z.number().int().min(0),
      z.number().int().min(0),
    ]),
    flipFrame: z.number().int().min(0),
    audioS3Key: z.string().trim().min(1),
  })
  .strict()
  .superRefine((scene, context) => {
    const expectedDuration = wordSceneFramesForRound(scene.round);
    if (scene.endFrame - scene.startFrame !== expectedDuration) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endFrame'],
        message: `${scene.round} word scene must be ${expectedDuration} frames`,
      });
    }

    const beatFrames = beatFramesForRound(scene.round);
    const expectedCues = scene.cueFrames.map(
      (_, index) => scene.startFrame + beatFrames * index,
    );
    if (scene.cueFrames.some((cue, index) => cue !== expectedCues[index])) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cueFrames'],
        message: `cueFrames must use the ${beatFrames}-frame ${scene.round} beat`,
      });
    }

    if (
      !(
        scene.cueFrames[1] < scene.flipFrame &&
        scene.flipFrame < scene.cueFrames[2]
      )
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['flipFrame'],
        message: 'flipFrame must be between teacher and child cues',
      });
    }
  });

export const wordChantTimelineSchema = z
  .object({
    version: z.literal('word_chant.timeline.v1'),
    fps: z.literal(24),
    durationFrames: z.number().int().positive(),
    durationSec: z.number().positive(),
    timingMode: z.literal('fixed_beat_atomic_audio'),
    scenes: z
      .array(
        z.union([
          wordChantStaticTimelineSceneSchema,
          wordChantWordTimelineSceneSchema,
        ]),
      )
      .min(9),
  })
  .strict()
  .superRefine((timeline, context) => {
    if (timeline.durationSec !== timeline.durationFrames / WORD_CHANT_FPS) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['durationSec'],
        message: 'durationSec must be derived from durationFrames at 24fps',
      });
    }

    const sceneIds = timeline.scenes.map((scene) => scene.sceneId);
    if (new Set(sceneIds).size !== sceneIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scenes'],
        message: 'sceneId must be unique',
      });
    }

    let cursor = 0;
    for (const scene of timeline.scenes) {
      if (scene.startFrame !== cursor) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scenes'],
          message: 'scenes must be contiguous and start at frame zero',
        });
        break;
      }
      cursor = scene.endFrame;
    }
    if (cursor !== timeline.durationFrames) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['durationFrames'],
        message: 'last scene endFrame must equal durationFrames',
      });
    }

    const staticScenes = timeline.scenes.filter(
      (scene) => scene.kind !== 'word',
    );
    const staticDurations = new Map(
      staticScenes.map((scene) => [
        scene.kind,
        scene.endFrame - scene.startFrame,
      ]),
    );
    if (
      staticScenes.length !== 3 ||
      staticDurations.size !== 3 ||
      staticDurations.get('intro') !== WORD_CHANT_INTRO_FRAMES ||
      staticDurations.get('interlude') !== WORD_CHANT_INTERLUDE_FRAMES ||
      staticDurations.get('outro') !== WORD_CHANT_OUTRO_FRAMES
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scenes'],
        message: 'intro, interlude, and outro frame durations are fixed',
      });
    }

    const wordScenes = timeline.scenes.filter(
      (scene): scene is z.infer<typeof wordChantWordTimelineSceneSchema> =>
        scene.kind === 'word',
    );
    const normalWordIds = wordScenes
      .filter((scene) => scene.round === 'normal')
      .map((scene) => scene.wordId);
    const fastWordIds = wordScenes
      .filter((scene) => scene.round === 'fast')
      .map((scene) => scene.wordId);
    if (
      normalWordIds.length !== fastWordIds.length ||
      normalWordIds.some((wordId, index) => wordId !== fastWordIds[index])
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scenes'],
        message: 'normal and fast rounds must use the same word order',
      });
    }
    if (new Set(normalWordIds).size !== normalWordIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scenes'],
        message: 'each word must appear once per round',
      });
    }

    const wordCount = normalWordIds.length;
    let expectedDuration: number | null = null;
    try {
      expectedDuration = calculateWordChantDurationFrames(wordCount);
    } catch {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scenes'],
        message: 'timeline must contain between 3 and 8 words',
      });
    }
    if (
      expectedDuration !== null &&
      timeline.durationFrames !== expectedDuration
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['durationFrames'],
        message: 'durationFrames does not match the Word Chant word count',
      });
    }

    const expectedTopology = [
      'intro',
      ...Array<string>(wordCount).fill('normal-word'),
      'interlude',
      ...Array<string>(wordCount).fill('fast-word'),
      'outro',
    ];
    const actualTopology = timeline.scenes.map((scene) =>
      scene.kind === 'word' ? `${scene.round}-word` : scene.kind,
    );
    if (
      actualTopology.length !== expectedTopology.length ||
      actualTopology.some((kind, index) => kind !== expectedTopology[index])
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scenes'],
        message:
          'scene order must be intro, normal words, interlude, fast words, outro',
      });
    }
  });

export function calculateWordChantDurationFrames(wordCount: number): number {
  if (
    !Number.isInteger(wordCount) ||
    wordCount < WORD_CHANT_MIN_WORD_COUNT ||
    wordCount > WORD_CHANT_MAX_WORD_COUNT
  ) {
    throw new RangeError(
      `wordCount must be between ${WORD_CHANT_MIN_WORD_COUNT} and ${WORD_CHANT_MAX_WORD_COUNT}`,
    );
  }

  return (
    WORD_CHANT_INTRO_FRAMES +
    WORD_CHANT_NORMAL_WORD_FRAMES * wordCount +
    WORD_CHANT_INTERLUDE_FRAMES +
    WORD_CHANT_FAST_WORD_FRAMES * wordCount +
    WORD_CHANT_OUTRO_FRAMES
  );
}

export function beatFramesForRound(round: 'normal' | 'fast'): number {
  return round === 'normal'
    ? WORD_CHANT_NORMAL_BEAT_FRAMES
    : WORD_CHANT_FAST_BEAT_FRAMES;
}

export function wordSceneFramesForRound(round: 'normal' | 'fast'): number {
  return round === 'normal'
    ? WORD_CHANT_NORMAL_WORD_FRAMES
    : WORD_CHANT_FAST_WORD_FRAMES;
}

export function wordChantFramesToSeconds(frame: number): number {
  if (!Number.isInteger(frame) || frame < 0) {
    throw new RangeError('frame must be a non-negative integer');
  }
  return frame / WORD_CHANT_FPS;
}

export type WordChantInput = z.infer<typeof wordChantInputSchema>;
export type WordChantAnalysis = z.infer<typeof wordChantAnalysisSchema>;
export type WordChantRecordedInput = z.infer<
  typeof wordChantRecordedInputSchema
>;
export type WordChantTimeline = z.infer<typeof wordChantTimelineSchema>;

export function createWordChantEditableCueSegments(
  cueSegments: WordChantAnalysis['cueSegments'],
): WordChantRecordedInput['cueSegments'] {
  return cueSegments.map((cue) => ({
    cueId: cue.cueId,
    detectedText: cue.text,
    text: resolveWordChantDefaultCueDisplayText(cue.text) ?? cue.text,
    isEdited: false,
    startMs: cue.startMs,
    endMs: cue.endMs,
  }));
}

export function reconcileWordChantEditableCueSegments(
  savedCueSegments: WordChantRecordedInput['cueSegments'],
  analysisCueSegments: WordChantAnalysis['cueSegments'],
  preserveSavedText: boolean,
): WordChantRecordedInput['cueSegments'] {
  const freshCueSegments =
    createWordChantEditableCueSegments(analysisCueSegments);
  if (
    !preserveSavedText ||
    savedCueSegments.length !== freshCueSegments.length
  ) {
    return freshCueSegments;
  }
  const savedById = new Map(savedCueSegments.map((cue) => [cue.cueId, cue]));
  const identitiesMatch = freshCueSegments.every((freshCue) => {
    const savedCue = savedById.get(freshCue.cueId);
    return (
      savedCue?.detectedText === freshCue.detectedText &&
      savedCue.startMs === freshCue.startMs &&
      savedCue.endMs === freshCue.endMs
    );
  });
  if (!identitiesMatch) return freshCueSegments;
  return freshCueSegments.map((freshCue) => ({
    ...freshCue,
    text: savedById.get(freshCue.cueId)?.text ?? freshCue.text,
    isEdited: savedById.get(freshCue.cueId)?.isEdited ?? freshCue.isEdited,
  }));
}
